import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import {
  AppSvgIconComponent,
  ColumnDef,
  SignalDataTableComponent,
  State,
  TableStateEvent,
} from '@vallift/ngl';
import { BaseComponent } from '@core/base/base-component';
import type { ListFormSubmissionsApiResponse } from '../../models/list-form-submissions';
import { FormSubmissionsApiService } from '../../services/form-submissions-api.service';
import { FormSubmissionAnswerCellComponent } from './form-submission-answer-cell';

@Component({
  selector: 'app-form-data-page',
  standalone: true,
  imports: [AppSvgIconComponent, RouterLink, SignalDataTableComponent],
  templateUrl: './form-data-page.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormDataPage extends BaseComponent {
  private readonly submissionsApi = inject(FormSubmissionsApiService);

  readonly tableState = new State<ListFormSubmissionsApiResponse>();

  readonly slug = computed(
    () => this.route.snapshot.paramMap.get('slug') ?? '',
  );

  readonly columnDefs = computed<ColumnDef[]>(() => {
    const apiColumns = this.tableState.response()?.data?.columns ?? [];

    const fixed: ColumnDef[] = [
      {
        title: 'Reference',
        key: 'reference',
        type: 'text',
        alignment: 'center',
        formatter: (value: unknown) =>
          typeof value === 'string' && value.trim() ? value : '—',
      },
      {
        title: 'Submitted',
        key: 'submittedAt',
        type: 'date',
        alignment: 'center',
      },
      {
        title: 'Status',
        key: 'status',
        type: 'text',
        alignment: 'center',
        formatter: (value: unknown) =>
          typeof value === 'string' ? value.replace(/_/g, ' ') : value,
      },
    ];

    const answerColumns: ColumnDef[] = apiColumns.map((column) => ({
      title: column.label,
      type: 'custom' as const,
      alignment: 'center',
      component: FormSubmissionAnswerCellComponent,
      customConfig: {
        data: { fieldId: column.fieldId },
      },
    }));

    return [...fixed, ...answerColumns];
  });

  onTableStateChanged(event: TableStateEvent): void {
    const slug = this.slug();
    if (!slug) {
      return;
    }

    const page = event.paginationEvent?.pageNumber ?? 1;
    const limit = event.paginationEvent?.pageSize ?? 200;

    this.executeRequest({
      state: this.tableState,
      request: this.submissionsApi.listFormSubmissions(slug, {
        page,
        limit,
      }),
      handleError: true,
    });
  }
}
